import type { CalculationType } from "@/graphql";
import calculationTypeVariant from "@/services/enums/calculationTypeVariant";
import type { BuildingUseType } from "../types/BuildingUseType";
import type { SelectableCalculationType } from "./calculationTypeDefaults";
import { suppressThresholdFallback } from "./thresholdFallback";

/** One area-capped row of a building type's limit value. */
export type LimitValueAreaBand = { threshold: number; maxTotalArea: number | null };

/**
 * The limit value a building type resolves to under a calculation type, shared by the start-project
 * dialog and the create wizard's Project details step.
 *
 *  - `value`  — one matching row, so the number is exact. 12 of BR18 (2025)'s 15 building types.
 *  - `area`   — one or more matching rows are capped by `maxTotalArea`, so no single number answers the
 *               question. Denmark's three holiday-home types (Sommerhus, Campinghytte, Anden feriebolig)
 *               are 4 up to 149 m² and 6.7 above. Printing only the low band and leaving the other one for
 *               later would read as a lie by omission, so every matching band is carried instead — the
 *               true, complete answer, sorted ascending with the uncapped row last.
 *  - `none`   — no row and no flat threshold. Custom and Level(s) carry none at all.
 */
export type LimitValue =
  | { kind: "value"; value: number }
  | { kind: "area"; bands: LimitValueAreaBand[] }
  | { kind: "weighted"; value: number; shares: WeightedLimitShare[] }
  | { kind: "none" };

/** One use's part of an area-weighted limit: its own limit, and its share of the uses' total area. */
export type WeightedLimitShare = { buildingTypeId: number; area: number; share: number; threshold: number; label?: string };

export type WeightedLimitValue = { value: number; shares: WeightedLimitShare[] };

/**
 * One band as the panel renders it. `maxTotalArea` is carried through unchanged because it is what
 * identifies a band — two bands cannot share a cap — so the list has something to key on that is not
 * a display string.
 */
export type LimitValueBandRow = { threshold: number; maxTotalArea: number | null; qualifier: string };

/**
 * Ascending bands as one row each, so both limits can carry the same large-number treatment a
 * single-value building type gets.
 *
 * The qualifier sits beside a 24px number in a 280px rail, so it is kept to the range itself. An
 * uncapped band is stated against the cap below it rather than as a bare "above".
 */
export const limitValueBandRows = (bands: LimitValueAreaBand[]): LimitValueBandRow[] =>
  bands.map((band, index) => {
    const previousCap = index > 0 ? bands[index - 1].maxTotalArea : null;

    if (band.maxTotalArea === null) {
      return { ...band, qualifier: previousCap === null ? "any area" : `above ${previousCap} m²` };
    }

    return {
      ...band,
      qualifier: previousCap === null ? `up to ${band.maxTotalArea} m²` : `${previousCap}–${band.maxTotalArea} m²`,
    };
  });

/**
 * The store action `resolveLimitValue` defers to for its "no matching row" fallback rule — the same
 * shape as `CalculationTypesProvider.getThresholds`, named locally so this file has no runtime
 * dependency on the store, only on whatever function its caller passes in.
 */
type GetThresholds = (
  calculationType: CalculationType,
  buildingTypeId: number,
  totalArea: number,
  noFallback: boolean,
  customThreshold?: number
) => { threshold?: number; installationalThreshold?: number };

/**
 * Resolves a building type's limit value under a calculation type.
 *
 * Extracted from the start-project dialog so the create wizard's Project details step can show the same
 * answer and keep it live as the building type changes there — two places computing "what does this
 * building type's limit value work out to" would drift the moment one of them was edited without the
 * other.
 *
 * `getThresholds` is passed in rather than imported, which is what keeps this a pure function: it is the
 * store action that already owns the "no per-building-type row matched, fall back to the flat threshold
 * unless told not to" rule (`CalculationTypesProvider.getThresholds`), and calling it here keeps that
 * rule single-authored instead of a second copy that could disagree with it.
 */
export const resolveLimitValue = (
  calculationType: SelectableCalculationType,
  buildingTypeId: number,
  country: string | null | undefined,
  getThresholds: GetThresholds
): LimitValue => {
  const rows = calculationType.thresholdByBuildingType ?? [];
  const matchingRows = rows.filter((row) => Number(row.buildingTypeId) === buildingTypeId);

  // Any capped row means the band is chosen by area, whether or not an uncapped row sits beside it.
  // Sorted ascending, the uncapped row last, so the sentence reads low-to-high regardless of the order
  // Hasura returned (the fragment orders `desc_nulls_last`, the opposite of what reads naturally here).
  if (matchingRows.some((row) => !!row.maxTotalArea)) {
    const bands = matchingRows
      .slice()
      .sort((bandA, bandB) => {
        if (!bandA.maxTotalArea) return 1;
        if (!bandB.maxTotalArea) return -1;
        return bandA.maxTotalArea - bandB.maxTotalArea;
      })
      .map((row) => ({ threshold: row.threshold, maxTotalArea: row.maxTotalArea ?? null }));
    return { kind: "area", bands };
  }

  // Normalised, not just cast: `getThresholds` calls `.find` on `thresholdByBuildingType` without
  // guarding it, so a calculation type that arrives without the field — a Storybook fixture, a narrower
  // query later — would throw inside the resolver. Passing `[]` keeps the resolver the single authority on
  // the fallback rule instead of reimplementing it here. The cast is the same one the wizard step uses
  // (CalculationTypeForm): the list row and the single fetched record differ only in fields it never reads.
  const resolvable = { ...calculationType, thresholdByBuildingType: rows } as CalculationType;

  const { threshold } = getThresholds(resolvable, buildingTypeId, 0, suppressThresholdFallback(country, calculationType.label));

  return threshold === null || threshold === undefined ? { kind: "none" } : { kind: "value", value: threshold };
};

/** Whether a calculation type averages a mixed-use building's limit by area (BR18 (2025), §298 stk. 5). */
export const weightsMixedUse = (calculationType: { variant?: { variant: string } | null } | null | undefined): boolean =>
  calculationType?.variant?.variant === calculationTypeVariant.BR182025;

// Four decimals: enough for any real limit, and it drops float noise such as 7.699999999.
const roundLimit = (value: number): number => Math.round(value * 10000) / 10000;

/** The area-weighted limit of a mixed-use building, Σ(areaᵢ × limitᵢ) / Σ areaᵢ, the same rule the API applies.
 * Null when it does not apply: not a BR182025 type, fewer than two uses, no area yet, or a use with no limit. */
export const resolveWeightedLimitValue = (
  calculationType: SelectableCalculationType,
  uses: BuildingUseType[] | null | undefined,
  getThresholds: GetThresholds,
  totalArea?: number
): WeightedLimitValue | null => {
  if (!weightsMixedUse(calculationType) || !uses || uses.length < 2) return null;

  const useArea = uses.reduce((sum, use) => sum + (use.area ?? 0), 0);
  if (useArea <= 0) return null;

  const resolvable = {
    ...calculationType,
    thresholdByBuildingType: calculationType.thresholdByBuildingType ?? [],
  } as CalculationType;
  // Area caps are matched against the whole building, never one use's share of it.
  const capArea = totalArea ?? useArea;

  const shares: WeightedLimitShare[] = [];
  for (const use of uses) {
    if (use.buildingTypeId === null) return null;
    const { threshold } = getThresholds(resolvable, Number(use.buildingTypeId), capArea, true);
    if (threshold === null || threshold === undefined) return null;
    const area = use.area ?? 0;
    shares.push({ buildingTypeId: Number(use.buildingTypeId), area, share: area / useArea, threshold });
  }

  const value = shares.reduce((sum, share) => sum + share.area * share.threshold, 0) / useArea;
  return { value: roundLimit(value), shares };
};
